import React from "react";
import { Link } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { ProductCard } from "@/components/ProductCard";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import {
  Star, Crown, Truck, Store, Building2, ShoppingBag,
  Sparkles, Heart, ArrowRight, ClipboardList, CreditCard, PackageCheck,
} from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/bb72bd7c-9835-4fa6-9cfb-53b102d6745e/images/f2890f955d4779362a58cb572c8279d0d925162fca950740c1856fcaf9b69b8d.jpeg";

const REVIEWS = [
  { name: "Tasha M.", text: "The Red Velvet Pecan popcorn is unreal. Tastes just like a slice of cake!" },
  { name: "Marcus D.", text: "Ordered cookies for the office and everyone begged for more. Mix & match is genius." },
  { name: "Kayla R.", text: "Cinnamon pecans are dangerously good. Warm, buttery, perfect. Ordering again!" },
  { name: "James P.", text: "CarMax delivery made my whole week. Fresh popcorn right at my desk." },
];

function SectionTitle({ script, title, sub }) {
  return (
    <div className="text-center mb-10">
      {script && <p className="font-script text-gold text-3xl mb-1">{script}</p>}
      <h2 className="font-serif text-3xl sm:text-4xl font-bold text-cream tracking-tight">{title}</h2>
      {sub && <p className="text-cream/60 mt-3 max-w-xl mx-auto">{sub}</p>}
    </div>
  );
}

export default function Home() {
  const { products } = useProducts();
  const popcorn = products.filter((p) => p.category === "popcorn");
  const cookies = products.filter((p) => p.category === "cookie");
  const pecans = products.find((p) => p.category === "pecans");
  const featured = popcorn.filter((p) => p.featured).slice(0, 4);
  const bestSellers = products.filter((p) => p.best_seller).slice(0, 4);
  const { settings } = useAuth();

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        <img src={HERO} alt="Overflow Popcorn" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/40" />
        <div className="absolute inset-0 bg-burgundy-dark/30" />
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto animate-fade-up">
          <div className="flex justify-center mb-4">
            <Crown className="text-gold" size={44} strokeWidth={1.3} />
          </div>
          <p className="font-script text-gold text-3xl sm:text-4xl mb-2">Pop Into Happiness</p>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-black text-cream tracking-tight leading-none">
            OVERFLOW <span className="gold-gradient-text">POPCORN CO.</span>
          </h1>
          <p className="text-gold/90 tracking-[0.2em] uppercase text-sm sm:text-base mt-4 font-medium">
            Gourmet Flavor. Overflowing Joy.
          </p>
          <p className="text-cream/70 mt-4 max-w-lg mx-auto">
            Handcrafted gourmet popcorn, cinnamon pecans & cookies — made in small batches with Southern love.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/popcorn"><Button className="btn-gold rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto" data-testid="hero-shop-popcorn-btn"><ShoppingBag size={18} className="mr-2" />Shop Popcorn</Button></Link>
            <Link to="/cookies"><Button className="rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto bg-burgundy text-cream border border-gold/40 hover:bg-burgundy-dark" data-testid="hero-shop-cookies-btn">Shop Cookies</Button></Link>
            <Link to="/cinnamon-pecans"><Button className="rounded-full px-7 py-6 text-base font-semibold w-full sm:w-auto bg-transparent text-gold border border-gold/60 hover:bg-gold/10" data-testid="hero-shop-pecans-btn">Shop Cinnamon Pecans</Button></Link>
          </div>
        </div>
      </section>

      {/* FEATURED FLAVORS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionTitle script="Handcrafted with Love" title="Featured Flavors" sub="Our most-loved gourmet popcorn, coated fresh and overflowing with flavor." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
        <div className="text-center mt-10">
          <Link to="/popcorn"><Button variant="ghost" className="text-gold hover:text-gold-bright" data-testid="view-all-popcorn">View all popcorn flavors <ArrowRight size={16} className="ml-1" /></Button></Link>
        </div>
      </section>

      {/* CINNAMON PECANS SPOTLIGHT */}
      {pecans && (
        <section className="bg-burgundy-dark/60 border-y border-gold/20 py-16 sm:py-20" data-testid="home-pecans-spotlight">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative order-2 lg:order-1">
              <img src={pecans.image} alt="Cinnamon Pecans" className="rounded-2xl border border-gold/30 shadow-2xl w-full aspect-square object-cover" />
              <span className="absolute -top-3 -left-3 btn-gold px-4 py-2 rounded-full font-bold text-sm">Signature Roast</span>
            </div>
            <div className="order-1 lg:order-2">
              <Sparkles className="text-gold mb-3" size={28} />
              <p className="font-script text-gold text-3xl">More Than Popcorn…</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-cream mt-1">Cinnamon Pecans</h2>
              <p className="text-cream/70 mt-4 text-lg">Crispy, warm cinnamon pecans coated in a sweet, buttery glaze. Slow-roasted in small batches — a Southern favorite you can't put down.</p>
              <p className="font-mono text-gold text-2xl mt-4">From {money(Math.min(...(pecans.sizes || []).map((s) => s.price)))}</p>
              <Link to="/cinnamon-pecans"><Button className="btn-gold rounded-full px-7 py-6 mt-6 font-semibold" data-testid="pecans-spotlight-btn">Shop Cinnamon Pecans <ArrowRight size={16} className="ml-2" /></Button></Link>
            </div>
          </div>
        </section>
      )}

      {/* COOKIES TEASER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionTitle script="Cookies Make Life Sweeter" title="Gourmet Cookies" sub="Dare to Be Different. 2 for $6 — mix & match any flavor." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {cookies.slice(0, 6).map((c, i) => (
            <div key={c.id} className="gold-border-card rounded-xl overflow-hidden bg-card-surface animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="aspect-square overflow-hidden">
                <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
              </div>
              <p className="text-cream text-xs font-semibold p-2 text-center truncate">{c.name}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/cookies"><Button className="btn-gold rounded-full px-7 py-6 font-semibold" data-testid="view-all-cookies">Build Your Cookie Box <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </section>

      {/* BEST SELLERS */}
      {bestSellers.length > 0 && (
        <section className="bg-card-surface/40 border-y border-gold/15 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionTitle title="Best Sellers" sub="The crowd favorites everyone keeps coming back for." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {bestSellers.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* HOW ORDERING WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionTitle title="How Ordering Works" />
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: ClipboardList, t: "1. Pick Your Treats", d: "Choose popcorn, cookies & pecans. Select sizes and mix & match flavors." },
            { icon: CreditCard, t: "2. Pay with Cash App", d: "Checkout in seconds and pay securely with Cash App. Include your order number." },
            { icon: PackageCheck, t: "3. We Deliver Fresh", d: "Pick your delivery day & time window and we'll bring your gourmet snacks right to your door." },
          ].map((s) => (
            <div key={s.t} className="gold-border-card rounded-xl bg-card-surface p-7 text-center">
              <s.icon className="text-gold mx-auto mb-4" size={34} strokeWidth={1.5} />
              <h3 className="font-serif text-cream text-xl mb-2">{s.t}</h3>
              <p className="text-cream/60 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PICKUP & DELIVERY */}
      <section className="bg-burgundy-dark/60 border-y border-gold/20 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTitle title="Local Delivery" sub="Fresh gourmet snacks delivered right to your door." />
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Truck, t: "We Come to You", d: "Delivered fresh to your doorstep within our local delivery zones." },
              { icon: PackageCheck, t: "Pick Your Time", d: "Choose a delivery day and time window that works for you at checkout." },
              { icon: Heart, t: "Made Fresh", d: "Every order is handcrafted in small batches just before it's delivered." },
            ].map((s) => (
              <div key={s.t} className="gold-border-card rounded-xl bg-card-surface p-7">
                <s.icon className="text-gold mb-4" size={30} strokeWidth={1.5} />
                <h3 className="font-serif text-cream text-xl mb-2">{s.t}</h3>
                <p className="text-cream/60 text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <SectionTitle script="Something Delicious for Everyone" title="Customer Reviews" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {REVIEWS.map((r) => (
            <div key={r.name} className="gold-border-card rounded-xl bg-card-surface p-6">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} size={15} className="text-gold fill-gold" />)}
              </div>
              <p className="text-cream/80 text-sm italic">"{r.text}"</p>
              <p className="text-gold text-sm mt-3 font-semibold">— {r.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="gold-border-card rounded-2xl bg-burgundy/40 p-10 text-center">
          <Heart className="text-gold mx-auto mb-3 fill-gold" size={30} />
          <h2 className="font-serif text-3xl text-cream font-bold">Thank you for supporting Overflow Popcorn Co.!</h2>
          <p className="text-cream/70 mt-3">Questions or catering? Reach us at {settings?.email} or {settings?.social?.instagram}.</p>
          <Link to="/shop"><Button className="btn-gold rounded-full px-8 py-6 mt-6 font-semibold" data-testid="contact-cta-shop">Start Your Order</Button></Link>
        </div>
      </section>
    </div>
  );
}
